import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useDispatch } from "react-redux";
import { page, remove } from "./api-evento";
import { decreaseQuantAprovacaoEvento } from "./actions/actions";
import EventoForm from "./EventoForm";
import EventoStatusChip from "./EventoStatusChip";
import EventoTable from "./EventoTable";
import EventoTurmaTable from "../EventoTurma/EventoTurmaTable";
import EventoAlunoTable from "../EventoAluno/EventoAlunoTable";
import EngajamentoTable from "../EventoUsuario/EngajamentoTable";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

export default function EventoContainer() {
  const dispatch = useDispatch();
  const [evento, setEvento] = useState(null);
  const [eventos, setEventos] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  // Incrementado para recarregar a página atual (após criar/remover).
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    page(pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setEventos({ ...EMPTY_PAGE, ...data });
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [pageNumber, reloadKey]);

  const handleSaved = useCallback((data, eNovo) => {
    if (eNovo) {
      setPageNumber(0);
      setReloadKey((key) => key + 1);
      return;
    }
    setEventos((prevState) => ({
      ...prevState,
      content: prevState.content.map((e) => (e.codigo === data.codigo ? data : e)),
    }));
  }, []);

  const handleDelete = (alvo) =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover o evento "${alvo.titulo}"?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(alvo.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              // Evento pendente de aprovação deixa de contar nas pendências do menu.
              if (!alvo.aprovado && !alvo.enviado) dispatch(decreaseQuantAprovacaoEvento());
              setEvento(null);
              if (eventos.content.length === 1 && pageNumber > 0) {
                setPageNumber((p) => p - 1);
              } else {
                setReloadKey((key) => key + 1);
              }
              success("Evento removido.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  const destinatarios = evento?.codigo && (evento.enviarPor === "Turma" || evento.enviarPor === "Aluno");

  return (
    <Container>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        {evento ? (
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <Tooltip title="Voltar para a lista">
              <IconButton onClick={() => setEvento(null)} edge="start">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box minWidth={0}>
              <Typography variant="h6" noWrap>
                {evento.codigo ? evento.titulo || "Evento" : "Novo Evento"}
              </Typography>
              {evento.codigo && <EventoStatusChip evento={evento} />}
            </Box>
          </Stack>
        ) : (
          <>
            <Box>
              <Typography variant="h6">Eventos</Typography>
              <Typography variant="body2" color="text.secondary">
                Crie, aprove, envie e acompanhe as respostas dos eventos da instituição.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEvento({ eNovo: true })}>
              Novo Evento
            </Button>
          </>
        )}
      </Stack>

      {evento ? (
        <Stack spacing={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: destinatarios ? 8 : 12 }}>
              <EventoForm evento={evento} setEvento={setEvento} onSaved={handleSaved} onDelete={handleDelete} />
            </Grid>
            {destinatarios && (
              <Grid size={{ xs: 12, md: 4 }}>
                {evento.enviarPor === "Turma" ? (
                  <EventoTurmaTable evento={evento} />
                ) : (
                  <EventoAlunoTable evento={evento} />
                )}
              </Grid>
            )}
          </Grid>
          {evento.codigo && evento.enviado && <EngajamentoTable evento={evento} />}
        </Stack>
      ) : (
        <EventoTable
          eventos={eventos}
          isLoading={isLoading}
          onChangePage={(event, newPage) => setPageNumber(newPage)}
          onSelect={setEvento}
        />
      )}
    </Container>
  );
}
